'use client';

import React from 'react';
import { clampExcerpt } from '@/helpers/excerpt';
import {
  StyledContentWrapper,
  StyledSectionLabel,
  StyledSectionHeading,
  StyledSectionIntro,
  StyledSection,
  StyledInner,
  StyledStepsGrid,
  StyledStep,
  StyledFormulaBox,
  StyledFormulaBody,
  StyledDataTable,
  StyledBadge,
  StyledTableFootnote,
  StyledFactorsGrid,
  StyledFactorCard,
  StyledTipsGrid,
  StyledTipCard,
  StyledMetricsTable,
  StyledRelatedGrid,
  StyledRelatedCard,
} from './LandingPageGraderContent.styles';

interface IRelatedPost {
  _id?: string;
  name: string;
  slug: string;
  excerpt: string;
}

// ─── Data ─────────────────────────────────────────────────────────────────────

const steps = [
  {
    number: '1',
    title: 'Enter your page URL and answer the checklist',
    description:
      'Provide your landing page URL for reference, then answer 7 yes/no questions about the key conversion elements on your page: headline, CTA, social proof, trust badges, load time, mobile responsiveness, and form placement.',
  },
  {
    number: '2',
    title: 'Get your score out of 100',
    description:
      'Each factor is weighted by its proven impact on conversion rates. Your total score out of 100 tells you exactly how well your page is set up to convert visitors into leads and customers.',
  },
  {
    number: '3',
    title: 'See your breakdown and fix the gaps',
    description:
      'Your results show which factors you passed and which you failed, with the points earned for each. Focus on the highest-weighted missing elements first for the fastest impact. No sign-up required. Completely free.',
  },
];

const scoreBenchmarks = [
  { range: '80 - 100', label: 'Strong', notes: 'Your page covers the conversion essentials. Focus on A/B testing and advanced optimization.' },
  { range: '50 - 79', label: 'Needs Work', notes: 'You have the basics but critical elements are missing. Each fix can meaningfully lift your rate.' },
  { range: '0 - 49', label: 'Needs Fixes', notes: 'Significant gaps are hurting conversions. Start with headline, CTA, and mobile responsiveness.' },
];

const elementBenchmarks = [
  { element: 'Clear Headline', weight: '20 pts', impact: 'Very High', notes: 'Visitors decide to stay or leave within 3-5 seconds. A weak headline kills conversions before they start.' },
  { element: 'Single CTA', weight: '20 pts', impact: 'Very High', notes: 'Multiple CTAs cause decision paralysis. One clear action increases clicks by 20-40%.' },
  { element: 'Mobile Responsive', weight: '15 pts', impact: 'High', notes: 'Over 60% of traffic is mobile. Non-responsive pages lose the majority of potential leads.' },
  { element: 'Page Load Time', weight: '15 pts', impact: 'High', notes: 'Each second of delay reduces conversions by 7%. Under 2 seconds is the target.' },
  { element: 'Social Proof', weight: '15 pts', impact: 'High', notes: 'Testimonials, reviews, and case studies can lift conversion rates by 15-30%.' },
  { element: 'Trust Badges', weight: '10 pts', impact: 'Medium', notes: 'Security seals and guarantees reduce form submission anxiety and increase completions.' },
  { element: 'Form Above the Fold', weight: '5 pts', impact: 'Medium', notes: 'Visible forms without scrolling get 20% more submissions than those requiring scrolling.' },
];

const landingPageMistakes = [
  {
    icon: '📰',
    title: 'Headline that describes instead of persuades',
    description:
      'Most headlines tell visitors what the product is rather than what it does for them. A headline like "Project Management Software" loses to "Ship Projects On Time, Every Time." Lead with the outcome your audience wants, not the feature you are selling.',
    stat: 'Benefit-driven headlines increase conversions by 20-40%',
  },
  {
    icon: '🎯',
    title: 'Multiple competing calls to action',
    description:
      'When a landing page has three buttons, a navigation menu, a chat widget, and two links, visitors freeze. Every option you add dilutes the one action you actually want them to take. Strip the page down to a single primary CTA and watch your conversion rate climb.',
    stat: 'Removing nav links from landing pages increases conversions by 100%+',
  },
  {
    icon: '🐌',
    title: 'Slow load time destroying your paid traffic',
    description:
      'You pay for the click, but if the page takes 5 seconds to load, half your visitors are already gone. Mobile users on slower connections are especially likely to bounce. Compress images, remove unused scripts, and use a CDN before you spend another dollar on paid traffic.',
    stat: 'Pages loading in 1s convert 3x better than pages loading in 5s',
  },
  {
    icon: '📱',
    title: 'Desktop-only design in a mobile-first world',
    description:
      'A landing page that looks perfect on a 27-inch monitor may be completely unusable on a phone. Buttons too small to tap, text that requires zooming, and forms that break on mobile are all conversion killers. Always test your landing pages on actual mobile devices.',
    stat: '61% of users will not return to a site they had trouble accessing on mobile',
  },
  {
    icon: '🤔',
    title: 'Zero social proof or credibility signals',
    description:
      'Asking a stranger to hand over their email or credit card without showing any evidence that others have done so and been happy is a high-friction request. Customer testimonials, logos of recognizable brands you have worked with, and review counts all reduce the perceived risk of converting.',
    stat: '92% of consumers read online reviews before converting',
  },
  {
    icon: '📋',
    title: 'Forms that ask for too much information',
    description:
      'Every additional form field reduces submission rates. Asking for name, email, phone, company, industry, team size, and budget in a single form is asking your visitor to do a lot of work before they trust you. Start with just what you need to start a conversation and gather more data later.',
    stat: 'Reducing form fields from 11 to 4 can increase conversions by 120%',
  },
];

const tips = [
  {
    number: '01',
    title: 'Write headlines that lead with the outcome, not the feature',
    description:
      'Rewrite your headline so it names the specific result your visitor wants, not what your product does. Test at least 3 headline variants. Use numbers when possible: "Generate 3x More Leads from the Same Traffic" outperforms "Our Lead Generation Platform" every time.',
    cta: null,
    ctaHref: null,
  },
  {
    number: '02',
    title: 'Add social proof above the fold with real testimonials',
    description:
      'Place at least one customer testimonial, review count, or recognizable client logo above the fold so visitors see credibility signals before they even finish reading your headline. Specificity wins: a testimonial that names a result performs better than a generic one.',
    cta: 'Add Testimonials widget free',
    ctaHref: '/widgets/testimonials',
  },
  {
    number: '03',
    title: 'Create urgency with a countdown timer',
    description:
      'Limited-time offers with a visible countdown timer consistently outperform evergreen offers. Whether it is a trial expiry, a limited bonus, or a promotional price, displaying a real deadline shifts visitors from "I will think about it" to "I need to act now."',
    cta: 'Add Countdown Timer widget free',
    ctaHref: '/widgets/countdown',
  },
  {
    number: '04',
    title: 'Add trust badges to reduce form submission anxiety',
    description:
      'Security seals, money-back guarantee badges, and certification logos placed near your CTA button or form reduce the fear of clicking. Visitors who are unsure about giving you their information need to see credibility signals at the exact moment of decision.',
    cta: 'Add Trust Badges widget free',
    ctaHref: '/widgets/trust-badges',
  },
  {
    number: '05',
    title: 'Use exit-intent popups to recover leaving visitors',
    description:
      'Even well-optimized landing pages lose visitors. An exit-intent popup that triggers when a visitor moves their cursor toward the browser bar gives you one more chance to capture them before they are gone. Offer something compelling: a free resource, a discount, or a quick-start guide.',
    cta: 'Add Popup Builder widget free',
    ctaHref: '/widgets/popup-builder',
  },
  {
    number: '06',
    title: 'Compress images and eliminate render-blocking scripts',
    description:
      'Page speed is a conversion factor and a ranking factor. Use next-gen image formats (WebP), lazy-load images below the fold, defer non-critical JavaScript, and use a CDN. Run Google PageSpeed Insights before and after to measure the improvement.',
    cta: null,
    ctaHref: null,
  },
  {
    number: '07',
    title: 'Reduce your form to the minimum required fields',
    description:
      'Cut every form field that is not essential for the next step in your sales process. If you only need an email to start a trial or send a lead magnet, ask for only an email. You can enrich the data later. Fewer fields means more submissions from the same traffic.',
    cta: null,
    ctaHref: null,
  },
  {
    number: '08',
    title: 'A/B test your headline and CTA copy continuously',
    description:
      'Landing page optimization is never finished. Set up A/B tests for your headline, CTA button text, and page hero section. Run each test until you reach statistical significance, implement the winner, and immediately set up the next test. Continuous testing compounds your conversion rate over time.',
    cta: null,
    ctaHref: null,
  },
];

const glossaryMetrics = [
  {
    term: 'Landing Page Conversion Rate',
    definition: 'The percentage of visitors who complete the desired action on your landing page, such as submitting a form or starting a trial.',
    formula: '(Conversions / Visitors) x 100',
    whenToUse: 'Measuring overall landing page effectiveness and comparing page variants',
  },
  {
    term: 'Bounce Rate',
    definition: 'The percentage of visitors who land on your page and leave without taking any action. High bounce rates signal headline, relevance, or speed issues.',
    formula: '(Single-Page Sessions / Total Sessions) x 100',
    whenToUse: 'Diagnosing whether your page matches visitor intent and expectations',
  },
  {
    term: 'Above the Fold',
    definition: 'The portion of the webpage visible without scrolling. Content above the fold gets significantly more attention than content below it.',
    formula: 'N/A - layout concept',
    whenToUse: 'Deciding where to place your headline, CTA, and form for maximum visibility',
  },
  {
    term: 'CTA Click-Through Rate',
    definition: 'The percentage of page visitors who click your primary call-to-action button. A low CTR usually means the CTA copy, placement, or design needs work.',
    formula: '(CTA Clicks / Page Visitors) x 100',
    whenToUse: 'Isolating whether your CTA is the conversion bottleneck',
  },
  {
    term: 'Time on Page',
    definition: 'How long visitors spend on your landing page before leaving. Low time on page combined with low conversions suggests the page is not engaging or matching intent.',
    formula: 'Average Session Duration (analytics platform)',
    whenToUse: 'Diagnosing content engagement issues separate from form friction',
  },
];

// ─── Component ────────────────────────────────────────────────────────────────

interface IProps {
  relatedPosts: IRelatedPost[];
}

export const LandingPageGraderContent = ({ relatedPosts }: IProps) => {
  return (
    <StyledContentWrapper>

      {/* ── How It Works ── */}
      <StyledSection>
        <StyledInner>
          <StyledSectionLabel>How It Works</StyledSectionLabel>
          <StyledSectionHeading>How to use this free landing page grader</StyledSectionHeading>
          <StyledSectionIntro>
            No account needed, no sign-up required. Completely free. Answer 7 questions about your
            landing page and get your score out of 100 with a full factor-by-factor breakdown.
          </StyledSectionIntro>
          <StyledStepsGrid>
            {steps.map((step) => (
              <StyledStep key={step.number}>
                <div className="step-number">{step.number}</div>
                <h3>{step.title}</h3>
                <p>{step.description}</p>
              </StyledStep>
            ))}
          </StyledStepsGrid>
        </StyledInner>
      </StyledSection>

      {/* ── The Formula ── */}
      <StyledSection bg="var(--gray-50)">
        <StyledInner maxWidth="720px">
          <StyledSectionLabel>The Scoring Formula</StyledSectionLabel>
          <StyledSectionHeading>
            How the landing page score is calculated
          </StyledSectionHeading>
          <StyledSectionIntro>
            This free landing page grader weights each factor by its proven impact on conversion rates.
            Higher-weight elements have a bigger direct effect on whether visitors convert.
          </StyledSectionIntro>

          <StyledFormulaBox>
            <p className="label">Landing Page Score</p>
            <p className="formula">Score = Sum of all earned points (max 100)</p>
            <p className="example">
              Example: <span>Headline (20) + Single CTA (20) + Mobile (15) + Fast Load (15) + Social Proof (15) + Trust Badges (10) + Form Above Fold (5) = 100</span>
            </p>
          </StyledFormulaBox>

          <StyledFormulaBody>
            <p>
              The scoring model is designed around the elements with the highest measured impact on
              landing page conversion rates. Headline and CTA each carry 20 points because they are
              the two factors visitors encounter first and respond to most directly.
            </p>
            <p>
              Mobile responsiveness and page load time each carry 15 points because they affect
              whether a visitor can even engage with your page, particularly on mobile devices where
              over 60% of web traffic now originates. A perfectly written page that loads in 6 seconds
              on mobile will still lose most of its visitors before they read a word.
            </p>
            <p>
              Social proof rounds out the high-impact tier with 15 points. Trust badges add 10 points,
              and form placement above the fold contributes the final 5. A score of 80 or above means
              your page covers the proven conversion essentials. Below 50 means critical gaps are
              actively costing you leads every day.
            </p>
          </StyledFormulaBody>
        </StyledInner>
      </StyledSection>

      {/* ── Score Benchmarks ── */}
      <StyledSection>
        <StyledInner>
          <StyledSectionLabel>Score Benchmarks</StyledSectionLabel>
          <StyledSectionHeading>
            What your landing page score means in 2026
          </StyledSectionHeading>
          <StyledSectionIntro>
            Compare your score against these benchmarks to understand where your page stands
            and what to prioritize next.
          </StyledSectionIntro>

          <StyledDataTable>
            <table>
              <thead>
                <tr>
                  <th>Score Range</th>
                  <th>Status</th>
                  <th>What to Do Next</th>
                </tr>
              </thead>
              <tbody>
                {scoreBenchmarks.map((row) => (
                  <tr key={row.range}>
                    <td className="col-name">{row.range}</td>
                    <td>
                      <StyledBadge
                        color={row.label === 'Strong' ? '#166534' : row.label === 'Needs Work' ? '#92400e' : '#991b1b'}
                        bg={row.label === 'Strong' ? '#dcfce7' : row.label === 'Needs Work' ? '#fef3c7' : '#fee2e2'}
                      >
                        {row.label}
                      </StyledBadge>
                    </td>
                    <td className="col-note">{row.notes}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </StyledDataTable>

          <StyledTableFootnote>
            Score ranges based on conversion rate research from CXL, Unbounce, and Google, 2026.
          </StyledTableFootnote>
        </StyledInner>
      </StyledSection>

      {/* ── Elements by Weight ── */}
      <StyledSection bg="var(--gray-50)">
        <StyledInner>
          <StyledSectionLabel>Element Weights</StyledSectionLabel>
          <StyledSectionHeading>
            How each landing page element is weighted in 2026
          </StyledSectionHeading>
          <StyledSectionIntro>
            Understanding which elements carry the most weight helps you prioritize your optimization
            efforts for maximum conversion impact.
          </StyledSectionIntro>

          <StyledDataTable>
            <table>
              <thead>
                <tr>
                  <th>Element</th>
                  <th>Weight</th>
                  <th>Impact Level</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                {elementBenchmarks.map((row) => (
                  <tr key={row.element}>
                    <td className="col-name">{row.element}</td>
                    <td className="col-value">{row.weight}</td>
                    <td>
                      <StyledBadge
                        color={row.impact === 'Very High' ? '#1e40af' : row.impact === 'High' ? '#166534' : '#92400e'}
                        bg={row.impact === 'Very High' ? '#dbeafe' : row.impact === 'High' ? '#dcfce7' : '#fef3c7'}
                      >
                        {row.impact}
                      </StyledBadge>
                    </td>
                    <td className="col-note">{row.notes}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </StyledDataTable>

          <StyledTableFootnote>
            Weights derived from Unbounce, CXL, and Google conversion research, 2026.
          </StyledTableFootnote>
        </StyledInner>
      </StyledSection>

      {/* ── Common Mistakes ── */}
      <StyledSection>
        <StyledInner>
          <StyledSectionLabel>Common Mistakes</StyledSectionLabel>
          <StyledSectionHeading>
            Six landing page mistakes that kill conversions
          </StyledSectionHeading>
          <StyledSectionIntro>
            Most landing pages fail not because the offer is wrong, but because of fixable
            design and copy mistakes. These are the six most common conversion killers.
          </StyledSectionIntro>

          <StyledFactorsGrid>
            {landingPageMistakes.map((factor) => (
              <StyledFactorCard key={factor.title}>
                <span className="factor-icon">{factor.icon}</span>
                <h3>{factor.title}</h3>
                <p>{factor.description}</p>
                <span className="factor-stat">{factor.stat}</span>
              </StyledFactorCard>
            ))}
          </StyledFactorsGrid>
        </StyledInner>
      </StyledSection>

      {/* ── 8 Tips ── */}
      <StyledSection bg="var(--gray-50)">
        <StyledInner>
          <StyledSectionLabel>Improve Your Score</StyledSectionLabel>
          <StyledSectionHeading>
            8 proven tips to improve your landing page conversion rate
          </StyledSectionHeading>
          <StyledSectionIntro>
            Apply these strategies to turn more of your landing page visitors into leads and customers.
            All CommonNinja widgets mentioned below are free to start.
          </StyledSectionIntro>

          <StyledTipsGrid>
            {tips.map((tip) => (
              <StyledTipCard key={tip.number}>
                <div className="tip-header">
                  <div className="tip-number">{tip.number}</div>
                  <h3>{tip.title}</h3>
                </div>
                <p>{tip.description}</p>
                {tip.cta && tip.ctaHref && (
                  <a className="tip-cta" href={tip.ctaHref} target="_blank" rel="noopener noreferrer">
                    {tip.cta} →
                  </a>
                )}
              </StyledTipCard>
            ))}
          </StyledTipsGrid>
        </StyledInner>
      </StyledSection>

      {/* ── Metrics Glossary ── */}
      <StyledSection>
        <StyledInner>
          <StyledSectionLabel>Metrics Glossary</StyledSectionLabel>
          <StyledSectionHeading>
            Landing page metrics explained
          </StyledSectionHeading>
          <StyledSectionIntro>
            These are the key metrics for measuring and improving your landing page performance.
            Here is what each one means and when to use it.
          </StyledSectionIntro>

          <StyledMetricsTable>
            <table>
              <thead>
                <tr>
                  <th>Metric</th>
                  <th>Definition</th>
                  <th>Formula</th>
                  <th>When to Use</th>
                </tr>
              </thead>
              <tbody>
                {glossaryMetrics.map((row) => (
                  <tr key={row.term}>
                    <td className="metric-name">{row.term}</td>
                    <td>{row.definition}</td>
                    <td><span className="metric-formula">{row.formula}</span></td>
                    <td>{row.whenToUse}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </StyledMetricsTable>
        </StyledInner>
      </StyledSection>

      {/* ── Related Articles ── */}
      {relatedPosts.length > 0 && (
        <StyledSection bg="var(--gray-50)">
          <StyledInner>
            <StyledSectionLabel>From the Blog</StyledSectionLabel>
            <StyledSectionHeading>Further reading on landing page optimization</StyledSectionHeading>
            <StyledSectionIntro>
              Dig deeper into the strategies behind building landing pages that convert
              visitors into leads and customers at scale.
            </StyledSectionIntro>
            <StyledRelatedGrid>
              {relatedPosts.map((post) => (
                <StyledRelatedCard
                  key={post.slug}
                  href={`/blog/${post.slug}`}
                >
                  <h3>{post.name}</h3>
                  {post.excerpt && (
                    <p dangerouslySetInnerHTML={{ __html: clampExcerpt(post.excerpt) }} />
                  )}
                  <span className="read-more">Read article →</span>
                </StyledRelatedCard>
              ))}
            </StyledRelatedGrid>
          </StyledInner>
        </StyledSection>
      )}

    </StyledContentWrapper>
  );
};
